import { lookupsJson } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { policyTypes } from '@ourbranch/policy-types';
import { CAR_DIFF_PATHS_TO_OMIT, containsOmittedPath } from 'core/helpers/diff-paths';
import { currencyFormatter, capitalize } from 'core/helpers/formatters';

const subPathsMap = {
  'quote.homeClaimsForUW': 'Home Claims',
  'quote.homeClaimDates': 'Home Claims',
  'quote.homeViolations': 'Home Claims',
  'quote.home': 'Property details',
  'quote.drivers.0': 'driver',
  'quote.drivers.1': 'driver',
  'quote.drivers.2': 'driver',
  'quote.drivers.3': 'driver',
  'quote.drivers.4': 'driver',
  'quote.drivers.5': 'driver',
  'quote.drivers.6': 'driver',
  'quote.drivers.7': 'driver',
  'quote.drivers.8': 'driver',
  'quote.drivers.9': 'driver',

  'quote.nonDrivers.0': 'resident',
  'quote.nonDrivers.1': 'resident',
  'quote.nonDrivers.2': 'resident',
  'quote.nonDrivers.3': 'resident',
  'quote.nonDrivers.4': 'resident',
  'quote.nonDrivers.5': 'resident',
  'quote.nonDrivers.6': 'resident',
  'quote.nonDrivers.7': 'resident',
  'quote.nonDrivers.8': 'resident',
  'quote.nonDrivers.9': 'resident',
  'quote.cars.0': 'car0',
  'quote.cars.1': 'vehicle',
  'quote.cars.2': 'vehicle',
  'quote.cars.3': 'vehicle',
  'quote.cars.4': 'vehicle',
  'quote.cars.5': 'vehicle',
  'quote.cars.6': 'vehicle',
  'quote.cars.7': 'vehicle',
  'quote.cars.8': 'vehicle',
  'quote.cars.9': 'vehicle'
};

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\./gi;

const SKIP_PATH_PREFIXES = ['options.0.', 'options.1.', 'options.2.', 'options.3.'];
const SKIP_PATH_SUFFIXES = ['.amount'];
const SKIP_PATH_CONTAINS = ['replacementCostEstimate.itemized'];
const CLAIM_ARRAY_PATTERN = /^(?:quote\.homeClaimsForUW|quote\.(?:drivers|cars)\.\d+\.claims)\.(\d+)\.(.+)$/;
const AUTO_VIOLATION_PATTERN = /\.autoViolationDates$/;
const OUTSTANDING_RECALL_PATTERN = /^quote\.cars\.\d+\.hasOutstandingRecall$/;
const PERSON_PATH_PATTERN = /^(quote\.(?:drivers|nonDrivers))\.(\d+)/;
const CAR_PATH_PATTERN = /^quote\.cars\.(\d+)/;

const isUnwantedKey = (path) =>
  SKIP_PATH_PREFIXES.some((p) => path.startsWith(p)) ||
  SKIP_PATH_SUFFIXES.some((s) => path.endsWith(s)) ||
  SKIP_PATH_CONTAINS.some((s) => path.includes(s));

const stripUuid = (path) => path.replace(UUID_PATTERN, '');

const labelForPath = (path) => {
  const last = path.split('.').pop();
  return last.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
};

const sourceMap = {
  mvr: 'MVR',
  claims: 'Claims history'
};

export const typeToName = {
  home: 'Home',
  auto: 'Auto',
  renters: 'Renters',
  condo: 'Condo',
  monoline: 'Monoline auto',
  'monoline-home': 'Monoline home',
  'monoline-condo': 'Monoline condo'
};

const SECTION_DEFS = [
  {
    key: 'home',
    title: 'Home',
    match: (path) =>
      path.startsWith('quote.home.') ||
      path.startsWith('quote.homeClaimsForUW') ||
      path.startsWith('quote.homeClaimDates') ||
      path.startsWith('quote.homeViolations')
  },
  {
    key: 'people',
    title: 'People',
    match: (path) => path.startsWith('quote.drivers.') || path.startsWith('quote.nonDrivers.')
  },
  {
    key: 'cars',
    title: 'Cars',
    match: (path) => path.startsWith('quote.cars.')
  }
];

// sensitive pii lives in SENSITIVE_DIFF_PATHS_TO_OMIT (core/helpers/diff-paths) and is always omitted.
const pathsToOmit = [
  ...CAR_DIFF_PATHS_TO_OMIT,
  'cherre',
  'postBindMVR',
  'gotDrivingRecord',
  'addDate',
  'driverSource',
  'returned',
  'homeLocation',
  'address',
  'city',
  'state',
  'autoViolationDatesAsString',
  'county',
  'initial'
];

export const parseDiff = (offerDifference) => {
  if (!offerDifference) {
    return { added: {}, removed: {} };
  }
  try {
    return {
      added: JSON.parse(offerDifference.addedChanges ?? '{}'),
      removed: JSON.parse(offerDifference.removedChanges ?? '{}')
    };
  } catch {
    return { added: {}, removed: {} };
  }
};

const getSubSectionLabel = ({ matchedKey, mappedLabel, offer }) => {
  const personMatch = matchedKey.match(PERSON_PATH_PATTERN);
  if (personMatch) {
    const [, collectionPath, index] = personMatch;
    const collection = collectionPath === 'quote.drivers' ? offer?.quote?.drivers : offer?.quote?.nonDrivers;
    const person = collection?.[Number(index)];
    if (!person) {
      return { subSectionToAddKey: mappedLabel };
    }

    const fullName = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
    return { subSectionToAddKey: fullName || mappedLabel, primary: person.isPrimary, coApp: person.isCoApplicant };
  }

  const carMatch = matchedKey.match(CAR_PATH_PATTERN);
  if (carMatch) {
    const car = offer?.quote?.cars?.[Number(carMatch[1])];
    if (!car) {
      return { subSectionToAddKey: mappedLabel };
    }

    const carLabel = [car.make, car.model, car.year].filter(Boolean).join(' ').trim();
    return { subSectionToAddKey: carLabel || mappedLabel };
  }

  return { subSectionToAddKey: mappedLabel };
};

const HOME_PRODUCT = { label: 'Home', totalKey: 'homeTotal', policyType: 'home' };
const AUTO_PRODUCT = { label: 'Auto', totalKey: 'autoTotal', policyType: 'auto' };
const FARM_PRODUCT = { label: 'Farm', totalKey: 'farmTotal', policyType: 'farm' };

// Pick the product rows to render for the selected option's line-up. Farm offers
// price a Farm line from `farmTotal`; on a farm option the home/auto totals are
// null placeholders, so the default home+auto set would render them as bogus
// "$0.00" rows and never show the actual farm premium (#33659). isFarmBasedPolicyType
// covers only the monoline codes F/Z/Y, so the FA (Farm & Auto) bundle — a real,
// selectable product — is handled explicitly and adds the Auto line.
const productsForSelectedOption = (selectedOption) => {
  if (isFarmBasedPolicyType(selectedOption)) {
    return [FARM_PRODUCT];
  }
  if (selectedOption === policyTypes.FABundle) {
    return [FARM_PRODUCT, AUTO_PRODUCT];
  }
  return [HOME_PRODUCT, AUTO_PRODUCT];
};

export const buildPricingRows = ({ offer, removed, eligibilityChanges }) => {
  const selectedOption = offer?.quote?.selectedOption;
  const selectedIdx = offer?.options?.findIndex((opt) => opt?.type === selectedOption) ?? -1;
  if (selectedIdx < 0) {
    return [];
  }

  const selectedOpt = offer.options[selectedIdx];

  const newlyIneligible = (policy) =>
    eligibilityChanges.some((c) => c.policyType === policy && c.wasEligible === true && c.isEligible === false);

  return productsForSelectedOption(selectedOption).reduce((rows, { label, totalKey, policyType }) => {
    const oldKey = `options.${selectedIdx}.${totalKey}`;
    const show = oldKey in removed || selectedOpt?.[totalKey] !== undefined;
    if (show) {
      const ineligible = newlyIneligible(policyType);
      rows.push({
        label,
        oldValue: currencyFormatter(removed[oldKey]),
        newValue: ineligible ? 'Ineligible' : currencyFormatter(selectedOpt?.[totalKey]),
        newlyIneligible: ineligible
      });
    }
    return rows;
  }, []);
};

export const buildSectionChanges = ({ added, removed, offer }) => {
  const allKeys = Array.from(new Set([...Object.keys(added), ...Object.keys(removed)]))
    .filter((p) => !isUnwantedKey(p) && !containsOmittedPath(p, pathsToOmit))
    .sort();

  const sections = SECTION_DEFS.map((s) => ({ ...s, sections: {} }));
  for (const path of allKeys) {
    const section = sections.find((s) => s.match(path));
    if (!section) {
      continue;
    }

    const matchedKey = Object.keys(subPathsMap).find((k) => path.includes(k));
    const mappedLabel = subPathsMap[matchedKey];

    if (!mappedLabel) {
      continue;
    }

    const { subSectionToAddKey, primary, coApp } = getSubSectionLabel({ matchedKey, mappedLabel, offer });
    if (!subSectionToAddKey) {
      continue;
    }
    if (!section.sections[subSectionToAddKey]) {
      section.sections[subSectionToAddKey] = { primary, coApp, rows: [], claims: {} };
    }
    const subSection = section.sections[subSectionToAddKey];

    const claimMatch = path.match(CLAIM_ARRAY_PATTERN);
    if (claimMatch) {
      const [, claimIndex, fieldPath] = claimMatch;
      const newValue = added[path];
      if (newValue === null || newValue === undefined) {
        continue;
      }
      if (!subSection.claims[claimIndex]) {
        subSection.claims[claimIndex] = {};
      }
      subSection.claims[claimIndex][fieldPath] = String(newValue);

      continue;
    }

    if (AUTO_VIOLATION_PATTERN.test(path)) {
      const rawValue = added[path];
      if (rawValue === null || rawValue === undefined) {
        continue;
      }
      let violationsByDate;
      try {
        violationsByDate = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
      } catch {
        continue;
      }
      Object.entries(violationsByDate).forEach(([violationDate, violations]) => {
        (violations || []).forEach((violation, index) => {
          const source = violation.isCourtRecord ? 'Court records' : violation.source;
          subSection.rows.push({
            path: `${path}-${violationDate}-${index}`,
            label: `${lookupsJson.autoViolationClassDescriptions[violation.class]} (type ${violation.class})`,
            oldValue: null,
            newValue: null,
            subtitle: `${violationDate} • Source: ${sourceMap[source] ? sourceMap[source] : capitalize(source)}`
          });
        });
      });

      continue;
    }

    if (OUTSTANDING_RECALL_PATTERN.test(path) && (added[path] === true || added[path] === 'true')) {
      subSection.rows.push({
        path,
        label: 'Vehicle’s make/model has an outstanding recall'
      });

      continue;
    }

    const row = {
      path,
      label: labelForPath(stripUuid(path)),
      oldValue: removed[path] !== undefined ? String(removed[path]) : null,
      newValue: added[path] !== undefined ? String(added[path]) : null,
      subtitle: null
    };

    if (!row.newValue) {
      continue;
    }

    subSection.rows.push(row);
  }

  for (const section of sections) {
    for (const subSection of Object.values(section.sections)) {
      Object.keys(subSection.claims)
        .sort((a, b) => Number(a) - Number(b))
        .forEach((claimIndex) => {
          const claim = subSection.claims[claimIndex];

          subSection.rows.push({
            path: `claim-${claimIndex}`,
            label: `${capitalize(claim.lossTypeCode)} claim (type ${claim.claimGroup})`,
            oldValue: null,
            newValue: null,
            subtitle: `${claim.lossDate} • Source: Claim History`
          });
        });
      delete subSection.claims;
    }
  }

  for (const section of sections) {
    for (const key of Object.keys(section.sections)) {
      if (section.sections[key].rows.length === 0) {
        delete section.sections[key];
      }
    }
  }

  return sections.filter((s) => Object.keys(s.sections).length > 0);
};
